import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
  Badge,
  Select,
  Input,
  Spinner,
  Avatar,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Collapse,
  Divider,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";
import {
  GraduationCap,
  IndianRupee,
  Clock,
  AlertTriangle,
  TrendingUp,
  Users,
  BookOpen,
  Building2,
  UserCheck,
  RefreshCw,
  CheckCircle,
  XCircle,
  Hourglass,
  Filter,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getData } from "../../redux/actions/common.action";
import {
  ADMISSION_SECTION_KEYS,
  hasAdmissionSection,
  isApproverDesignation,
} from "../../utils/admissionAccess";
import { BASE_URL } from "../../config/RequestMethod";
import axios from "axios";
import Cookies from "js-cookie";

// ─── helpers ────────────────────────────────────────────────────────────────
const formatCurrency = (n: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const formatBatchValue = (v: string) => {
  if (!v) return "-";
  if (/^\d{4}-\d{2}$/.test(v)) {
    const [yyyy, mm] = v.split("-");
    return `${mm}/${yyyy}`;
  }
  return v;
};

const formatDate = (d: any) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";

const STATUS_COLORS: Record<string, string> = {
  Approved: "#22C55E",
  Pending: "#F59E0B",
  Rejected: "#EF4444",
};

const PIE_PALETTE = ["#6366F1", "#22C55E", "#F59E0B", "#EF4444", "#0EA5E9", "#D946EF"];

// ─── KPI card ────────────────────────────────────────────────────────────────
const KpiCard = ({
  label,
  value,
  icon,
  gradient,
  sub,
}: {
  label: string;
  value: string;
  icon: any;
  gradient: string;
  sub?: string;
}) => (
  <Box
    bg="white"
    borderRadius="2xl"
    border="1px solid"
    borderColor="gray.100"
    p={5}
    boxShadow="sm"
    position="relative"
    overflow="hidden"
    transition="all 0.25s"
    _hover={{ transform: "translateY(-4px)", boxShadow: "md" }}
  >
    <Box
      position="absolute"
      top="-20px"
      right="-20px"
      w="90px"
      h="90px"
      borderRadius="full"
      bgGradient={gradient}
      opacity={0.12}
    />
    <HStack justify="space-between" align="start">
      <VStack align="start" spacing={1}>
        <Text fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase" letterSpacing="wide">
          {label}
        </Text>
        <Text fontSize="2xl" fontWeight="800" color="gray.800">
          {value}
        </Text>
        {sub && (
          <Text fontSize="10px" color="gray.400">
            {sub}
          </Text>
        )}
      </VStack>
      <Box p={3} bgGradient={gradient} borderRadius="xl" boxShadow="sm">
        <Icon as={icon} boxSize={5} color="white" />
      </Box>
    </HStack>
  </Box>
);

// ─── breakdown table row ──────────────────────────────────────────────────────
const BreakdownRow = ({
  rank,
  name,
  count,
  revenue,
  maxCount,
}: {
  rank: number;
  name: string;
  count: number;
  revenue?: number;
  maxCount: number;
}) => {
  const pct = maxCount > 0 ? Math.round((count / maxCount) * 100) : 0;
  return (
    <Tr _hover={{ bg: "gray.50" }}>
      <Td py={2} px={3}>
        <Text fontSize="xs" fontWeight="700" color="gray.500">
          #{rank}
        </Text>
      </Td>
      <Td py={2} px={3}>
        <Text fontSize="xs" fontWeight="600" color="gray.700" isTruncated maxW="140px">
          {name}
        </Text>
      </Td>
      <Td py={2} px={3}>
        <HStack spacing={2}>
          <Box flex={1} bg="gray.100" borderRadius="full" h="6px" minW="60px">
            <Box
              bg="blue.400"
              h="6px"
              borderRadius="full"
              w={`${pct}%`}
              transition="width 0.5s"
            />
          </Box>
          <Text fontSize="xs" fontWeight="700" color="blue.600" minW="20px">
            {count}
          </Text>
        </HStack>
      </Td>
      {revenue !== undefined && (
        <Td py={2} px={3} isNumeric>
          <Text fontSize="xs" color="green.600" fontWeight="600">
            {formatCurrency(revenue)}
          </Text>
        </Td>
      )}
    </Tr>
  );
};

// ─── main component ──────────────────────────────────────────────────────────
const AdmissionDashboard = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { branches, courses, users, signinuser } = useSelector(
    (s: any) => s.common,
  );
  const canOpenAdmissionList =
    hasAdmissionSection(signinuser, ADMISSION_SECTION_KEYS.VIEW) ||
    isApproverDesignation(signinuser);

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Global API filters
  const [branchId, setBranchId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [counsellorId, setCounsellorId] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Local (client-side) search filters for each breakdown card
  const [branchSearch, setBranchSearch] = useState("");
  const [courseSearch, setCourseSearch] = useState("");
  const [counsellorSearch, setCounsellorSearch] = useState("");
  const [createdBySearch, setCreatedBySearch] = useState("");

  useEffect(() => {
    dispatch(getData("branch"));
    dispatch(getData("course"));
    dispatch(getData("user"));
  }, [dispatch]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const token = Cookies.get("token");
      const params = new URLSearchParams();
      if (branchId) params.append("branchId", branchId);
      if (courseId) params.append("courseId", courseId);
      if (counsellorId) params.append("counsellorId", counsellorId);
      if (createdBy) params.append("createdBy", createdBy);
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);

      const res = await axios.get(`${BASE_URL}admission/dashboard?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data.Data);
    } catch (e) {
      console.error("Dashboard fetch error", e);
    } finally {
      setLoading(false);
    }
  }, [branchId, courseId, counsellorId, createdBy, startDate, endDate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const kpi = data?.kpi || {};

  // Only show active users (isActive !== false)
  const activeUsers = users?.filter((u: any) => u.isActive !== false) || [];
  const counsellorOptions = activeUsers.filter((u: any) => u.UserType === 3);

  // Client-side filtered breakdown rows
  const filteredByBranch = (data?.byBranch || []).filter((r: any) =>
    !branchSearch || r.name?.toLowerCase().includes(branchSearch.toLowerCase())
  );
  const filteredByCourse = (data?.byCourse || []).filter((r: any) =>
    !courseSearch || r.name?.toLowerCase().includes(courseSearch.toLowerCase())
  );
  const filteredByCounsellor = (data?.byCounsellor || []).filter((r: any) =>
    !counsellorSearch || r.name?.toLowerCase().includes(counsellorSearch.toLowerCase())
  );
  const filteredByCreatedBy = (data?.byCreatedBy || []).filter((r: any) =>
    !createdBySearch || r.name?.toLowerCase().includes(createdBySearch.toLowerCase())
  );

  return (
    <Box bg="gray.50" minH="100vh">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        position="relative"
        bgGradient="linear(to-br, purple.600, pink.500, orange.400)"
        overflow="hidden"
        mb={2}
      >
        {/* Animated blur orbs — same as MarketingDashboard */}
        <Box
          position="absolute"
          top="-50%"
          right="-10%"
          w="500px"
          h="500px"
          borderRadius="full"
          bg="whiteAlpha.100"
          filter="blur(80px)"
          animation="float 20s infinite ease-in-out"
        />
        <Box
          position="absolute"
          bottom="-30%"
          left="-5%"
          w="400px"
          h="400px"
          borderRadius="full"
          bg="whiteAlpha.100"
          filter="blur(60px)"
          animation="float 25s infinite ease-in-out reverse"
        />

        <style>{`
          @keyframes float {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            33%       { transform: translate(30px, -30px) rotate(5deg); }
            66%       { transform: translate(-20px, 20px) rotate(-5deg); }
          }
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(30px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        <Container maxW="8xl" position="relative" zIndex={1}>
          <VStack spacing={4} align="start" py={{ base: 6, md: 6 }} px={{ base: 4, md: 8 }}>
            <HStack
              bg="whiteAlpha.200"
              backdropFilter="blur(10px)"
              px={4}
              py={2}
              borderRadius="full"
              border="1px"
              borderColor="whiteAlpha.300"
              animation="slideUp 0.6s ease-out"
            >
              <Icon as={GraduationCap} boxSize={4} color="yellow.300" />
              <Text fontSize="sm" color="white" fontWeight="medium">
                Admission Analytics & Insights
              </Text>
            </HStack>

            <VStack align="start" spacing={4}>
              <Heading
                as="h1"
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                fontWeight="black"
                color="white"
                lineHeight="1.2"
                textShadow="0 4px 20px rgba(0,0,0,0.2)"
                animation="slideUp 0.8s ease-out"
              >
                Admission Dashboard
              </Heading>

              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="whiteAlpha.900"
                maxW="2xl"
                lineHeight="1.7"
                animation="slideUp 1s ease-out"
              >
                Monitor admissions, track fee collections, identify overdue payments, and analyse
                performance across branches, courses, and counsellors.
              </Text>

              <HStack spacing={3} pt={1} animation="slideUp 1.1s ease-out">
                {canOpenAdmissionList && (
                  <Button
                    size="sm"
                    bg="whiteAlpha.200"
                    color="white"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                    borderRadius="full"
                    _hover={{ bg: "whiteAlpha.300", transform: "translateY(-1px)" }}
                    _active={{ transform: "translateY(0)" }}
                    onClick={() => navigate("/admission-list")}
                  >
                    Go to Admission List
                  </Button>
                )}
              </HStack>
            </VStack>
          </VStack>
        </Container>

        {/* Wave divider — same as MarketingDashboard */}
        <Box position="absolute" bottom="-20px" left="0" right="0" height="60px">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
            <path
              d="M0,0 C150,100 350,0 600,50 C850,100 1050,0 1200,50 L1200,120 L0,120 Z"
              fill="#F7FAFC"
            />
          </svg>
        </Box>
      </Box>

      <Container maxW="8xl" px={{ base: 4, md: 8 }} pb={12} pt={4}>
        {loading && !data ? (
          <Flex justify="center" align="center" h="300px">
            <VStack>
              <Spinner size="xl" color="blue.500" thickness="3px" />
              <Text color="gray.500" fontSize="sm">Loading dashboard...</Text>
            </VStack>
          </Flex>
        ) : (
          <>
            {/* ── KPI cards ─────────────────────────────────────────────── */}
            <SimpleGrid columns={{ base: 2, md: 4 }} gap={4} mb={6}>
              <KpiCard
                label="Total Admissions"
                value={String(kpi.totalAdmissions || 0)}
                icon={GraduationCap}
                gradient="linear(to-br, blue.400, blue.600)"
                sub={`${kpi.approvedCount || 0} approved`}
              />
              <KpiCard
                label="Total Revenue"
                value={formatCurrency(kpi.totalRevenue)}
                icon={IndianRupee}
                gradient="linear(to-br, purple.400, purple.600)"
                sub={`${kpi.installmentCount || 0} instalments`}
              />
              <KpiCard
                label="Payment Collected"
                value={formatCurrency(kpi.collectedAmount)}
                icon={CheckCircle}
                gradient="linear(to-br, green.400, green.600)"
                sub={`${Math.round(kpi.totalRevenue ? (kpi.collectedAmount / kpi.totalRevenue) * 100 : 0)}% of total`}
              />
              <KpiCard
                label="Pending Payment"
                value={formatCurrency(kpi.pendingAmount)}
                icon={Clock}
                gradient="linear(to-br, orange.400, orange.500)"
                sub={`Overdue: ${formatCurrency(kpi.overdueAmount)}`}
              />
            </SimpleGrid>

            {/* ── Status strip ─────────────────────────────────────────── */}
            <SimpleGrid columns={{ base: 3, md: 3 }} gap={4} mb={6}>
              {[
                { label: "Approved", count: kpi.approvedCount, icon: CheckCircle, color: "green" },
                { label: "Pending Approval", count: kpi.pendingCount, icon: Hourglass, color: "orange" },
                { label: "Rejected", count: kpi.rejectedCount, icon: XCircle, color: "red" },
              ].map((s) => (
                <Box
                  key={s.label}
                  bg="white"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor={`${s.color}.100`}
                  p={4}
                  boxShadow="sm"
                >
                  <HStack>
                    <Icon as={s.icon} boxSize={5} color={`${s.color}.500`} />
                    <VStack align="start" spacing={0}>
                      <Text fontSize="xs" color="gray.500">{s.label}</Text>
                      <Text fontSize="xl" fontWeight="800" color={`${s.color}.600`}>
                        {s.count || 0}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              ))}
            </SimpleGrid>

            {/* ── Toolbar: Filter toggle + Refresh ─────────────────────── */}
            <Flex justify="space-between" align="center" mb={3}>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  variant={showFilters ? "solid" : "outline"}
                  colorScheme="blue"
                  leftIcon={<Icon as={Filter} boxSize={3} />}
                  rightIcon={<Icon as={showFilters ? ChevronUp : ChevronDown} boxSize={3} />}
                  onClick={() => setShowFilters((v) => !v)}
                  borderRadius="lg"
                >
                  {showFilters ? "Hide Filters" : "Show Filters"}
                </Button>
                {(branchId || courseId || counsellorId || createdBy || startDate || endDate) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => {
                      setBranchId(""); setCourseId(""); setCounsellorId("");
                      setCreatedBy(""); setStartDate(""); setEndDate("");
                    }}
                  >
                    Clear
                  </Button>
                )}
              </HStack>
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                leftIcon={<Icon as={RefreshCw} boxSize={3} />}
                onClick={fetchDashboard}
                isLoading={loading}
                loadingText="Refreshing..."
                borderRadius="lg"
              >
                Refresh
              </Button>
            </Flex>

            {/* ── Collapsible filter panel ──────────────────────────────── */}
            <Collapse in={showFilters} animateOpacity>
              <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="blue.100" p={4} mb={5} boxShadow="sm">
                <HStack mb={3} spacing={2}>
                  <Icon as={Filter} boxSize={4} color="blue.500" />
                  <Text fontSize="sm" fontWeight="700" color="gray.700">Global Filters</Text>
                  <Text fontSize="xs" color="gray.400">(applies to all charts &amp; metrics)</Text>
                </HStack>

                {/* Date range — highlighted */}
                <Box
                  bg="blue.50"
                  border="1px solid"
                  borderColor="blue.100"
                  borderRadius="xl"
                  px={4}
                  py={3}
                  mb={3}
                >
                  <HStack mb={2} spacing={1}>
                    <Icon as={TrendingUp} boxSize={3} color="blue.500" />
                    <Text fontSize="xs" fontWeight="700" color="blue.700">Chart Date Range</Text>
                  </HStack>
                  <SimpleGrid columns={{ base: 2, md: 2 }} gap={3} maxW="400px">
                    <FormControl>
                      <FormLabel fontSize="xs" mb={1} color="blue.700">From</FormLabel>
                      <Input size="sm" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} borderRadius="lg" bg="white" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="xs" mb={1} color="blue.700">To</FormLabel>
                      <Input size="sm" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} borderRadius="lg" bg="white" />
                    </FormControl>
                  </SimpleGrid>
                </Box>

                {/* Entity filters */}
                <SimpleGrid columns={{ base: 2, md: 4 }} gap={3}>
                  <FormControl>
                    <FormLabel fontSize="xs" mb={1}>Branch</FormLabel>
                    <Select size="sm" placeholder="All Branches" value={branchId} onChange={(e) => setBranchId(e.target.value)} borderRadius="lg">
                      {branches?.map((b: any) => (
                        <option key={b._id} value={b._id}>{b.BranchName}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" mb={1}>Course</FormLabel>
                    <Select size="sm" placeholder="All Courses" value={courseId} onChange={(e) => setCourseId(e.target.value)} borderRadius="lg">
                      {courses?.map((c: any) => (
                        <option key={c._id} value={c._id}>{c.CourseName}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" mb={1}>Counsellor</FormLabel>
                    <Select size="sm" placeholder="All Counsellors" value={counsellorId} onChange={(e) => setCounsellorId(e.target.value)} borderRadius="lg">
                      {counsellorOptions.map((u: any) => (
                        <option key={u._id} value={u._id}>{u.Name}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs" mb={1}>Created By</FormLabel>
                    <Select size="sm" placeholder="All Users" value={createdBy} onChange={(e) => setCreatedBy(e.target.value)} borderRadius="lg">
                      {activeUsers.map((u: any) => (
                        <option key={u._id} value={u._id}>{u.Name}</option>
                      ))}
                    </Select>
                  </FormControl>
                </SimpleGrid>

                <Flex justify="flex-end" mt={3} gap={2}>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setBranchId(""); setCourseId(""); setCounsellorId("");
                      setCreatedBy(""); setStartDate(""); setEndDate("");
                    }}
                  >
                    Clear All
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    leftIcon={<Icon as={RefreshCw} boxSize={3} />}
                    onClick={() => { fetchDashboard(); setShowFilters(false); }}
                    isLoading={loading}
                    loadingText="Applying..."
                  >
                    Apply &amp; Refresh
                  </Button>
                </Flex>
              </Box>
            </Collapse>

            {/* ── Charts row ───────────────────────────────────────────── */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6} mb={6}>
              {/* Monthly trend */}
              <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.100" p={5} boxShadow="sm">
                <HStack mb={4}>
                  <Icon as={TrendingUp} boxSize={4} color="blue.500" />
                  <Text fontWeight="700" color="gray.700" fontSize="sm">
                    Monthly Admission Trend
                  </Text>
                </HStack>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={data?.monthlyTrend || []} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <RTooltip
                      formatter={(val: any, name: string) =>
                        name === "revenue" ? formatCurrency(val) : val
                      }
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name="Admissions"
                      stroke="#6366F1"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>

              {/* Status pie */}
              <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.100" p={5} boxShadow="sm">
                <HStack mb={4}>
                  <Icon as={Hourglass} boxSize={4} color="purple.500" />
                  <Text fontWeight="700" color="gray.700" fontSize="sm">
                    Admission Status Distribution
                  </Text>
                </HStack>
                {data?.byStatus?.length ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={data.byStatus}
                        dataKey="count"
                        nameKey="_id"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ _id, percent }: any) =>
                          `${_id} ${(percent * 100).toFixed(0)}%`
                        }
                        labelLine={false}
                      >
                        {data.byStatus.map((entry: any, i: number) => (
                          <Cell
                            key={entry._id}
                            fill={STATUS_COLORS[entry._id] || PIE_PALETTE[i % PIE_PALETTE.length]}
                          />
                        ))}
                      </Pie>
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <RTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Flex align="center" justify="center" h="220px">
                    <Text color="gray.400" fontSize="sm">No data</Text>
                  </Flex>
                )}
              </Box>

              {/* Revenue bar by branch */}
              <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.100" p={5} boxShadow="sm">
                <HStack mb={4}>
                  <Icon as={Building2} boxSize={4} color="teal.500" />
                  <Text fontWeight="700" color="gray.700" fontSize="sm">
                    Revenue by Branch
                  </Text>
                </HStack>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data?.byBranch || []} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RTooltip
                      formatter={(val: any) => formatCurrency(val)}
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    />
                    <Bar dataKey="revenue" name="Revenue" fill="#14B8A6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              {/* Admissions bar by course */}
              <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.100" p={5} boxShadow="sm">
                <HStack mb={4}>
                  <Icon as={BookOpen} boxSize={4} color="indigo.500" />
                  <Text fontWeight="700" color="gray.700" fontSize="sm">
                    Admissions by Course
                  </Text>
                </HStack>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data?.byCourse || []} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <RTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Bar dataKey="count" name="Admissions" fill="#6366F1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </SimpleGrid>

            {/* ── Breakdown tables ──────────────────────────────────────── */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={5} mb={6}>

              {/* By Branch */}
              <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.100" boxShadow="sm" overflow="hidden">
                <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
                  <HStack mb={2}>
                    <Icon as={Building2} boxSize={4} color="teal.500" />
                    <Text fontSize="sm" fontWeight="700" color="gray.700">Branch-wise</Text>
                  </HStack>
                  <Select
                    size="xs"
                    placeholder="All Branches"
                    value={branchSearch}
                    onChange={(e) => setBranchSearch(e.target.value)}
                    borderRadius="md"
                  >
                    {branches?.map((b: any) => (
                      <option key={b._id} value={b.BranchName}>{b.BranchName}</option>
                    ))}
                  </Select>
                </Box>
                <Table size="sm" variant="unstyled">
                  <Thead>
                    <Tr>
                      <Th fontSize="9px" px={3} py={2}>#</Th>
                      <Th fontSize="9px" px={3} py={2}>Branch</Th>
                      <Th fontSize="9px" px={3} py={2}>Count</Th>
                      <Th fontSize="9px" px={3} py={2} isNumeric>Revenue</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredByBranch.length === 0 ? (
                      <Tr><Td colSpan={4} textAlign="center" py={6} fontSize="xs" color="gray.400">No data</Td></Tr>
                    ) : (
                      filteredByBranch.map((row: any, i: number) => (
                        <BreakdownRow
                          key={i}
                          rank={i + 1}
                          name={row.name}
                          count={row.count}
                          revenue={row.revenue}
                          maxCount={filteredByBranch[0]?.count || 1}
                        />
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>

              {/* By Course */}
              <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.100" boxShadow="sm" overflow="hidden">
                <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
                  <HStack mb={2}>
                    <Icon as={BookOpen} boxSize={4} color="purple.500" />
                    <Text fontSize="sm" fontWeight="700" color="gray.700">Course-wise</Text>
                  </HStack>
                  <Select
                    size="xs"
                    placeholder="All Courses"
                    value={courseSearch}
                    onChange={(e) => setCourseSearch(e.target.value)}
                    borderRadius="md"
                  >
                    {courses?.map((c: any) => (
                      <option key={c._id} value={c.CourseName}>{c.CourseName}</option>
                    ))}
                  </Select>
                </Box>
                <Table size="sm" variant="unstyled">
                  <Thead>
                    <Tr>
                      <Th fontSize="9px" px={3} py={2}>#</Th>
                      <Th fontSize="9px" px={3} py={2}>Course</Th>
                      <Th fontSize="9px" px={3} py={2}>Count</Th>
                      <Th fontSize="9px" px={3} py={2} isNumeric>Revenue</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredByCourse.length === 0 ? (
                      <Tr><Td colSpan={4} textAlign="center" py={6} fontSize="xs" color="gray.400">No data</Td></Tr>
                    ) : (
                      filteredByCourse.map((row: any, i: number) => (
                        <BreakdownRow
                          key={i}
                          rank={i + 1}
                          name={row.name}
                          count={row.count}
                          revenue={row.revenue}
                          maxCount={filteredByCourse[0]?.count || 1}
                        />
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>

              {/* By Counsellor */}
              <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.100" boxShadow="sm" overflow="hidden">
                <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
                  <HStack mb={2}>
                    <Icon as={UserCheck} boxSize={4} color="blue.500" />
                    <Text fontSize="sm" fontWeight="700" color="gray.700">Counsellor-wise</Text>
                  </HStack>
                  <Select
                    size="xs"
                    placeholder="All Counsellors"
                    value={counsellorSearch}
                    onChange={(e) => setCounsellorSearch(e.target.value)}
                    borderRadius="md"
                  >
                    {counsellorOptions.map((u: any) => (
                      <option key={u._id} value={u.Name}>{u.Name}</option>
                    ))}
                  </Select>
                </Box>
                <Table size="sm" variant="unstyled">
                  <Thead>
                    <Tr>
                      <Th fontSize="9px" px={3} py={2}>#</Th>
                      <Th fontSize="9px" px={3} py={2}>Counsellor</Th>
                      <Th fontSize="9px" px={3} py={2}>Count</Th>
                      <Th fontSize="9px" px={3} py={2} isNumeric>Revenue</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredByCounsellor.length === 0 ? (
                      <Tr><Td colSpan={4} textAlign="center" py={6} fontSize="xs" color="gray.400">No data</Td></Tr>
                    ) : (
                      filteredByCounsellor.map((row: any, i: number) => (
                        <BreakdownRow
                          key={i}
                          rank={i + 1}
                          name={row.name}
                          count={row.count}
                          revenue={row.revenue}
                          maxCount={filteredByCounsellor[0]?.count || 1}
                        />
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>

              {/* By Created By */}
              <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.100" boxShadow="sm" overflow="hidden">
                <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
                  <HStack mb={2}>
                    <Icon as={Users} boxSize={4} color="orange.500" />
                    <Text fontSize="sm" fontWeight="700" color="gray.700">Created By</Text>
                  </HStack>
                  <Select
                    size="xs"
                    placeholder="All Users"
                    value={createdBySearch}
                    onChange={(e) => setCreatedBySearch(e.target.value)}
                    borderRadius="md"
                  >
                    {activeUsers.map((u: any) => (
                      <option key={u._id} value={u.Name}>{u.Name}</option>
                    ))}
                  </Select>
                </Box>
                <Table size="sm" variant="unstyled">
                  <Thead>
                    <Tr>
                      <Th fontSize="9px" px={3} py={2}>#</Th>
                      <Th fontSize="9px" px={3} py={2}>User</Th>
                      <Th fontSize="9px" px={3} py={2}>Count</Th>
                      <Th fontSize="9px" px={3} py={2} isNumeric>Revenue</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredByCreatedBy.length === 0 ? (
                      <Tr><Td colSpan={4} textAlign="center" py={6} fontSize="xs" color="gray.400">No data</Td></Tr>
                    ) : (
                      filteredByCreatedBy.map((row: any, i: number) => (
                        <BreakdownRow
                          key={i}
                          rank={i + 1}
                          name={row.name}
                          count={row.count}
                          revenue={row.revenue}
                          maxCount={filteredByCreatedBy[0]?.count || 1}
                        />
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </SimpleGrid>

            {/* ── Recent Admissions table ────────────────────────────────── */}
            <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.100" boxShadow="sm" overflow="hidden">
              <HStack px={5} py={4} borderBottom="1px solid" borderColor="gray.100" justify="space-between">
                <HStack>
                  <Icon as={GraduationCap} boxSize={4} color="blue.500" />
                  <Text fontWeight="700" color="gray.700" fontSize="sm">Recent Admissions</Text>
                  <Badge colorScheme="blue" borderRadius="full" px={2} fontSize="10px">
                    Last 10
                  </Badge>
                </HStack>
              </HStack>
              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th fontSize="10px">Student</Th>
                      <Th fontSize="10px">Admission ID</Th>
                      <Th fontSize="10px">Course</Th>
                      <Th fontSize="10px">Branch</Th>
                      <Th fontSize="10px">Session</Th>
                      <Th fontSize="10px">Counsellor</Th>
                      <Th fontSize="10px">Status</Th>
                      <Th fontSize="10px" isNumeric>Fee</Th>
                      <Th fontSize="10px">Payment</Th>
                      <Th fontSize="10px">Date</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(data?.recentAdmissions || []).length === 0 ? (
                      <Tr>
                        <Td colSpan={10} textAlign="center" py={10} color="gray.400" fontSize="sm">
                          No admissions found
                        </Td>
                      </Tr>
                    ) : (
                      (data?.recentAdmissions || []).map((adm: any) => (
                        <Tr key={adm._id} _hover={{ bg: "gray.50" }}>
                          <Td py={2}>
                            <HStack spacing={2}>
                              <Avatar size="xs" name={adm.name} />
                              <VStack align="start" spacing={0}>
                                <Text fontSize="xs" fontWeight="700" color="gray.700">{adm.name}</Text>
                                <Text fontSize="9px" color="gray.400">{adm.mobileNo}</Text>
                              </VStack>
                            </HStack>
                          </Td>
                          <Td py={2}>
                            <Text fontSize="xs" fontFamily="monospace" fontWeight="700" color="gray.600">
                              {adm.admissionId || "-"}
                            </Text>
                          </Td>
                          <Td py={2}>
                            <Text fontSize="xs" isTruncated maxW="100px">{adm.courseId?.CourseName || "-"}</Text>
                          </Td>
                          <Td py={2}>
                            <Text fontSize="xs">{adm.branchId?.BranchName || "-"}</Text>
                          </Td>
                          <Td py={2}>
                            {adm.sessionFrom || adm.sessionTo ? (
                              <Text fontSize="xs" color="purple.600" fontWeight="600">
                                {formatBatchValue(adm.sessionFrom || "")} – {formatBatchValue(adm.sessionTo || "")}
                              </Text>
                            ) : (
                              <Text fontSize="xs" color="gray.400">-</Text>
                            )}
                          </Td>
                          <Td py={2}>
                            <Text fontSize="xs">{adm.counsellorId?.Name || "-"}</Text>
                          </Td>
                          <Td py={2}>
                            <Badge
                              colorScheme={
                                adm.admStatus === "Approved"
                                  ? "green"
                                  : adm.admStatus === "Rejected"
                                  ? "red"
                                  : "orange"
                              }
                              fontSize="9px"
                              borderRadius="full"
                              px={2}
                              py={0.5}
                            >
                              {adm.admStatus}
                            </Badge>
                          </Td>
                          <Td py={2} isNumeric>
                            <Text fontSize="xs" fontWeight="600" color="green.600">
                              {formatCurrency(adm.totalCoursePrice)}
                            </Text>
                          </Td>
                          <Td py={2}>
                            <Badge
                              colorScheme={adm.paymentType === "Full" ? "blue" : "purple"}
                              fontSize="9px"
                              borderRadius="full"
                              px={2}
                            >
                              {adm.paymentType}
                            </Badge>
                          </Td>
                          <Td py={2}>
                            <Text fontSize="xs" color="gray.500">{formatDate(adm.createdAt)}</Text>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
};

export default AdmissionDashboard;
